import React, { useState, useEffect } from 'react';
import FirstLoginPasswordChange from './FirstLoginPasswordChange';
import FirstLoginMFASetup from './FirstLoginMFASetup';
import FirstLoginEULAAcceptance from './FirstLoginEULAAcceptance';
import { useAuth } from '../contexts/AuthContext';

interface FirstLoginFlowProps {
  tempToken: string;
  userId: string;
  onComplete: () => void;
}

type FlowStep = 'password-change' | 'mfa-setup' | 'eula-acceptance' | 'complete';

const FirstLoginFlow: React.FC<FirstLoginFlowProps> = ({
  tempToken,
  userId,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState<FlowStep>('password-change');
  const [authToken, setAuthToken] = useState<string>('');

  const handlePasswordChanged = async (authToken: string) => {
    if (!authToken) {
      // No auth token provided, complete the flow without MFA
      // User will need to log in again and can set up MFA later
      console.log('[FirstLoginFlow] No auth token received, completing without MFA setup');
      setCurrentStep('complete');
      onComplete();
      return;
    }
    
    // We have an auth token, proceed to MFA setup
    console.log('[FirstLoginFlow] Auth token received, proceeding to MFA setup');
    console.log('[FirstLoginFlow] Token value:', authToken ? `${authToken.substring(0, 20)}...` : 'NO TOKEN');
    
    // Store the token in localStorage so MFA setup can use it
    localStorage.setItem('riskninja-token', authToken);
    
    setAuthToken(authToken);
    setCurrentStep('mfa-setup');
  };

  const handleMFAComplete = () => {
    setCurrentStep('eula-acceptance');
  };

  const handleMFASkip = () => {
    setCurrentStep('eula-acceptance');
  };

  const handleEULAComplete = () => {
    setCurrentStep('complete');
    onComplete();
  };

  switch (currentStep) {
    case 'password-change':
      return (
        <FirstLoginPasswordChange
          tempToken={tempToken}
          onPasswordChanged={handlePasswordChanged}
        />
      );
    
    case 'mfa-setup':
      return (
        <FirstLoginMFASetup
          authToken={authToken}
          onComplete={handleMFAComplete}
          onSkip={handleMFASkip}
        />
      );
    
    case 'eula-acceptance':
      return (
        <FirstLoginEULAAcceptance
          authToken={authToken}
          onComplete={handleEULAComplete}
        />
      );

    case 'complete':
      return null; // This case should trigger onComplete and redirect
    
    default:
      return null;
  }
};

export default FirstLoginFlow;